import { VirtualCardType } from 'modules/virtual-cards/constants'
import type { VcnProviderAvailability } from 'modules/virtual-cards/useVcnProviderAvailability'

import {
  VcnManagementAction,
  type VcnBulkUploadPreviewItem,
  type VcnBulkUploadPreviewResultRow,
} from '../../api/dto/vcn-bulk-upload-preview.dto'

/** UUID display form: first block + last 8 (`cc8e48fb...5eac97a3`); full value stays in tooltip/copy. */
export function middleEllipsis(value: string): string {
  if (value.length <= 14) return value
  return `${value.slice(0, 8)}...${value.slice(-8)}`
}

export function normalizeRowKeys(
  row: Record<string, string>
): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(row)) {
    out[k.trim().toLowerCase()] = String(v ?? '').trim()
  }
  return out
}

export function cell(
  norm: Record<string, string>,
  ...aliases: string[]
): string {
  for (const a of aliases) {
    const v = norm[a.toLowerCase()]
    if (v) return v
  }
  return ''
}

export function parseAction(raw: string): VcnManagementAction | null {
  const s = raw.trim().toLowerCase()
  if (s === VcnManagementAction.TERMINATE) return VcnManagementAction.TERMINATE
  if (s === VcnManagementAction.TERMINATE_AND_NEW)
    return VcnManagementAction.TERMINATE_AND_NEW
  if (s === VcnManagementAction.CREATE_NEW)
    return VcnManagementAction.CREATE_NEW
  return null
}

/** Empty cell → undefined (backend defaults to WEX); unknown value → null (row error). */
export function parseProvider(raw: string): VirtualCardType | undefined | null {
  const s = raw.trim().toUpperCase()
  if (!s) return undefined
  if (s === VirtualCardType.WEX) return VirtualCardType.WEX
  if (s === VirtualCardType.MARQETA) return VirtualCardType.MARQETA
  return null
}

export interface ParsedCsvRows {
  items: VcnBulkUploadPreviewItem[]
  /** 1-based CSV line numbers (header = line 1) with an invalid/missing action. */
  badActions: number[]
  /** 1-based CSV line numbers with an unrecognized provider value. */
  badProviders: number[]
  /** 1-based CSV line numbers asking for a provider whose safety switch is off. */
  disabledProviders: number[]
  missingBenefitIdCount: number
}

/**
 * Maps raw PapaParse rows to preview items, collecting per-row problems.
 * `availability` gates rows that would CREATE a card; terminate rows carry no
 * provider meaning and are never blocked by a provider safety switch.
 */
export function parseCsvRows(
  data: Record<string, string>[],
  availability: VcnProviderAvailability
): ParsedCsvRows {
  const items: VcnBulkUploadPreviewItem[] = []
  const badActions: number[] = []
  const badProviders: number[] = []
  const disabledProviders: number[] = []
  let missingBenefitIdCount = 0
  data.forEach((raw, index) => {
    const norm = normalizeRowKeys(raw)
    const benefitId = cell(norm, 'benefitid', 'benefit_id', 'benefit')
    if (!benefitId) {
      missingBenefitIdCount++
      return
    }
    const action = parseAction(cell(norm, 'action'))
    if (!action) {
      badActions.push(index + 2)
      return
    }
    const provider = parseProvider(cell(norm, 'provider'))
    if (provider === null) {
      badProviders.push(index + 2)
      return
    }
    const createsCard = action !== VcnManagementAction.TERMINATE
    // A blank cell means WEX, so a WEX outage blocks blank create rows too.
    if (
      createsCard &&
      !availability.isProviderEnabled(provider ?? VirtualCardType.WEX)
    ) {
      disabledProviders.push(index + 2)
      return
    }
    items.push({ benefitId, action, provider })
  })
  return {
    items,
    badActions,
    badProviders,
    disabledProviders,
    missingBenefitIdCount,
  }
}

export interface DedupedItems {
  deduped: VcnBulkUploadPreviewItem[]
  /** Duplicate benefitId with a DIFFERENT action — first occurrence kept. */
  actionConflictCount: number
  /** Duplicate benefitId, SAME action, different provider — first occurrence kept. */
  providerConflictCount: number
  /** Exact duplicates (same benefitId + action + provider) removed. Blank provider ≡ WEX. */
  sameDupCount: number
}

/** Blank cell and explicit WEX are the same request (backend defaults omitted provider to WEX). */
function providerDedupeKey(
  provider: VirtualCardType | undefined
): VirtualCardType {
  return provider ?? VirtualCardType.WEX
}

/**
 * Preview rows that would issue a card on a switched-off provider. The CSV was
 * gated at parse time, but a safety switch can flip while the preview sits on
 * screen — so Run re-checks against the rows the backend would actually get.
 * A blank provider cell means WEX; terminate rows carry no provider meaning.
 */
export function countRowsOnDisabledProviders(
  rows: VcnBulkUploadPreviewResultRow[],
  availability: VcnProviderAvailability
): number {
  return rows.filter(
    (row) =>
      row.requestedAction !== VcnManagementAction.TERMINATE &&
      !availability.isProviderEnabled(
        row.requestedProvider ?? VirtualCardType.WEX
      )
  ).length
}

export function dedupeItems(items: VcnBulkUploadPreviewItem[]): DedupedItems {
  const seen = new Map<string, { action: string; provider: VirtualCardType }>()
  const deduped: VcnBulkUploadPreviewItem[] = []
  let actionConflictCount = 0
  let providerConflictCount = 0
  let sameDupCount = 0
  for (const item of items) {
    const provider = providerDedupeKey(item.provider)
    const kept = seen.get(item.benefitId)
    if (!kept) {
      seen.set(item.benefitId, { action: item.action, provider })
      deduped.push(item)
    } else if (kept.action !== item.action) {
      actionConflictCount++
    } else if (kept.provider !== provider) {
      providerConflictCount++
    } else {
      sameDupCount++
    }
  }
  return {
    deduped,
    actionConflictCount,
    providerConflictCount,
    sameDupCount,
  }
}
